import { ReactElement, useMemo } from "react";
import { CalendarCell } from "./CalendarCell";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { isBetween } from "client/DateAndTime/dateUtils";
import { CalendarHighlightDateOnlyRange, isCalendarDateSelectable } from "./Calendar";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import styles from "./Week.module.less";
import { formatDateOnly } from "client_legacy/ndaw/dateFormatting";
import { KeyEventArguments } from "../TextInput/TextInput";

export type WeekProps = {
	dates: readonly DateOnly[];
	today: DateOnly;
	highlightRange: CalendarHighlightDateOnlyRange | null;
	hoverRange: CalendarHighlightDateOnlyRange | null;
	onSelect(value: DateOnly): void;
	onHover(value: DateOnly | null): void;
	eventDateMap: Map<DateOnly, boolean>;
	onOutOfRangeEventCheck?: (date: DateOnly) => boolean;
	minDate: DateOnly | null;
	maxDate: DateOnly | null;
	focusableDate: DateOnly;
	setItemRef: (item: HTMLElement | null, date: DateOnly) => void;
	onMovement: (args: KeyEventArguments, selectedDate: DateOnly) => boolean;
};

export const Week = (props: WeekProps): ReactElement => {
	const {
		dates,
		today,
		highlightRange,
		hoverRange,
		onSelect,
		onHover,
		eventDateMap,
		onOutOfRangeEventCheck = () => false,
		minDate,
		maxDate,
		focusableDate,
		onMovement,
		setItemRef
	} = props;

	const isActiveStates = useMemo(
		(): readonly boolean[] => {
			return dates.map(
				x => isNotNil(highlightRange) && isBetween(
					x,
					highlightRange.start,
					highlightRange.end
				)
			);
		},
		[dates, highlightRange]
	);

	const isHoveredStates = useMemo(
		(): readonly boolean[] => {
			return dates.map(
				x => isNotNil(hoverRange) && isBetween(
					x,
					hoverRange.start,
					hoverRange.end
				)
			);
		},
		[dates, hoverRange]
	);

	return (
		<div
			className={styles.root}
			role="row"
		>
			{dates.map((x, i) => {
				const isOutOfRange: boolean = onOutOfRangeEventCheck(x);
				const isDisabled: boolean = !isCalendarDateSelectable(
					x,
					minDate,
					maxDate
				);

				return (
					<CalendarCell
						trackingId={generateTrackingId("day", `|${x.toISOString()}`)}
						key={x.toISOString()}
						kind="normal"
						onClick={() => onSelect(x)}
						isDisabled={isDisabled}
						onMouseEnter={() => onHover(x)}
						onMouseLeave={() => onHover(null)}
						isOutOfRange={isOutOfRange}
						isActive={isActiveStates[i]}
						isNextActive={isActiveStates[i + 1] ?? false}
						isPreviousActive={isActiveStates[i - 1] ?? false}
						isHovered={isHoveredStates[i]}
						isNextHovered={isHoveredStates[i + 1] ?? false}
						isPreviousHovered={isHoveredStates[i - 1] ?? false}
						isToday={x.isEqualTo(today)}
						hasEvent={!isOutOfRange && (eventDateMap.get(x) ?? false)}
						tabIndex={x.isEqualTo(focusableDate)
							? 0
							: -1
						}
						ref={(el) => setItemRef(el, x)}
						onKeyDown={args => onMovement(args, x)}
						ariaLabel={formatDateOnly(x, "DM")}
					>
						{x.day}
					</CalendarCell>
				);
			})}
		</div>
	);
};
Week.displayName = "Week";